/* Portal app shell — slate sidebar + topbar. Ported from the Claude design's
   PortalShell; the demo role-switcher is replaced by a real user menu. */

import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Ico } from './Icons';
import { Avatar } from './ui';
import { useAuth } from '@/portal/auth/AuthContext';
import { initialsOf } from '@/portal/data/time';

const LOGO = '/assets/didiers-logo.svg';

const NAV = [
  {
    label: 'Workspace',
    items: [
      { key: 'portfolios', label: 'My Portfolios', icon: 'grid' as const, path: '/portal' },
      { key: 'collection', label: 'My Collection', icon: 'collection' as const, path: '/portal/collection' },
      { key: 'shared', label: 'Shared With Me', icon: 'share' as const, path: '/portal/shared' },
    ],
  },
];

function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const name = user?.name || user?.username || 'Account';
  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <button className="pl-roleswitch" onClick={() => setOpen((o) => !o)}>
        <Avatar initials={initialsOf(name)} sm />
        <strong style={{ fontWeight: 600 }}>{name}</strong>
        <Ico.chevDown width={14} height={14} />
      </button>
      {open && (
        <div className="pl-menu">
          <button
            onClick={() => {
              setOpen(false);
              navigate('/');
            }}
          >
            <span>View website</span>
          </button>
          <button
            onClick={() => {
              setOpen(false);
              navigate('/portal/change-password');
            }}
          >
            <span>Change password</span>
          </button>
          <button
            onClick={() => {
              setOpen(false);
              void logout();
            }}
          >
            <span style={{ color: 'var(--purple)' }}>Sign out</span>
          </button>
        </div>
      )}
    </div>
  );
}

export function PortalShell({
  title,
  crumb,
  search,
  onSearch,
  actions,
  wide,
  children,
}: {
  title: string;
  crumb?: string | null;
  search?: string | null;
  onSearch?: (v: string) => void;
  actions?: ReactNode;
  wide?: boolean;
  children: ReactNode;
}) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const activeKey = (() => {
    if (pathname === '/portal' || pathname.startsWith('/portal/portfolio')) return 'portfolios';
    if (pathname.startsWith('/portal/collection')) return 'collection';
    if (pathname.startsWith('/portal/shared')) return 'shared';
    return 'portfolios';
  })();

  const name = user?.name || user?.username || 'Account';

  return (
    <div className="portal">
      <div className="pl-shell">
        <aside className="pl-side">
          <div className="pl-side-logo">
            <img src={LOGO} alt="Didier's Patisserie" />
          </div>
          <nav className="pl-nav">
            {NAV.map((grp) => (
              <div key={grp.label}>
                <div className="pl-nav-label">{grp.label}</div>
                {grp.items.map((it) => {
                  const IconC = Ico[it.icon];
                  return (
                    <button
                      key={it.key}
                      className={'pl-nav-item' + (activeKey === it.key ? ' active' : '')}
                      onClick={() => navigate(it.path)}
                    >
                      <IconC width={17} height={17} />
                      {it.label}
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>
          <div className="pl-side-user">
            <Avatar initials={initialsOf(name)} />
            <div>
              <div className="nm">{name}</div>
              <div className="rl">Client &amp; Trade</div>
            </div>
          </div>
        </aside>

        <div className="pl-main">
          <header className="pl-topbar">
            <h2>{title}</h2>
            {crumb && <span className="pl-crumb">{crumb}</span>}
            <div className="pl-spacer" />
            {onSearch && (
              <div className="pl-search">
                <Ico.search width={16} height={16} />
                <input
                  placeholder="Search products…"
                  value={search ?? ''}
                  onChange={(e) => onSearch(e.target.value)}
                />
              </div>
            )}
            {actions}
            <UserMenu />
          </header>
          <div className={'pl-content' + (wide ? ' wide' : '')}>{children}</div>
        </div>
      </div>
    </div>
  );
}
